import {
  ArrowRight,
  CheckCircle,
  Eye,
  EyeSlash,
  LockKey,
  ShieldCheck,
  WifiHigh,
  X,
} from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";

import { getApiErrorMessage, useSubmitWifiLink } from "@/lib/api";

import { TRUST_POINTS } from "./copy";

const TRUST_ICONS = [ShieldCheck, CheckCircle, X];

interface CredentialsFormProps {
  token: string;
  identityAnswer: string;
  onSubmitted: (networkName: string) => void;
}

export function CredentialsForm({ token, identityAnswer, onSubmitted }: CredentialsFormProps) {
  const [networkName, setNetworkName] = useState("");
  const [password, setPassword] = useState("");
  const [hasSplitBands, setHasSplitBands] = useState(false);
  const [networkName5ghz, setNetworkName5ghz] = useState("");
  const [confirmedMainNetwork, setConfirmedMainNetwork] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    networkName?: string;
    password?: string;
    confirmation?: string;
    form?: string;
  }>({});

  const submit = useSubmitWifiLink();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: typeof errors = {};
    if (!networkName.trim()) {
      nextErrors.networkName = "Enter your main Wi-Fi network name.";
    }
    if (password.length < 8) {
      nextErrors.password = "Enter the Wi-Fi password (at least 8 characters).";
    }
    if (!confirmedMainNetwork) {
      nextErrors.confirmation = "Confirm this is your main home network before sharing.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      await submit.mutateAsync({
        token,
        body: {
          identity_answer: identityAnswer,
          wifi_ssid: networkName.trim(),
          wifi_password: password,
          has_split_bands: hasSplitBands,
          wifi_ssid_5ghz: hasSplitBands && networkName5ghz.trim() ? networkName5ghz.trim() : null,
          confirmed_main_network: confirmedMainNetwork,
        },
      });
      setPassword("");
      onSubmitted(networkName.trim());
    } catch (err) {
      setErrors({
        form: getApiErrorMessage(err, "We could not save that. Please try again."),
      });
    }
  };

  return (
    <div className="max-w-[570px]">
      <p className="text-accent mb-4 text-[12px] tracking-[0.1em] uppercase">
        Before we pack your kit
      </p>
      <h1 className="text-primary text-[42px] leading-[1.06] tracking-[-0.035em] sm:text-[56px] lg:text-[62px]">
        Share your home Wi-Fi, with clarity.
      </h1>
      <p className="text-muted mt-6 max-w-[520px] text-[18px] leading-7">
        We need your main network details to prepare the two connectors in your Smart Climate kit. A
        guest network will not work because it keeps devices apart.
      </p>

      <form onSubmit={handleSubmit} noValidate className="mt-9">
        <div className="space-y-5 rounded-2xl bg-white p-5 shadow-[0_16px_50px_-34px_rgba(40,35,31,0.45)] sm:p-7">
          <div>
            <label htmlFor="network-name" className="text-primary block text-[14px] leading-5">
              Main Wi-Fi network name
            </label>
            <p className="text-muted mt-1 text-[12px] leading-4">
              Sometimes called the Wi-Fi name or SSID.
            </p>
            <div className="relative mt-2">
              <WifiHigh
                className="text-muted absolute top-1/2 left-4 size-5 -translate-y-1/2"
                weight="light"
                aria-hidden
              />
              <input
                id="network-name"
                name="network-name"
                value={networkName}
                onChange={(event) => {
                  setNetworkName(event.target.value);
                  setErrors((current) => ({ ...current, networkName: undefined }));
                }}
                autoComplete="off"
                aria-invalid={Boolean(errors.networkName)}
                aria-describedby={errors.networkName ? "network-name-error" : undefined}
                placeholder="e.g. HomeWiFi"
                className="border-border text-primary placeholder:text-muted focus:border-ring focus:ring-accent/15 h-13 w-full rounded-xl border bg-white pr-4 pl-12 text-[16px] transition outline-none focus:ring-4"
              />
            </div>
            {errors.networkName && (
              <p id="network-name-error" className="text-destructive mt-2 text-[12px] leading-4">
                {errors.networkName}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="network-password" className="text-primary block text-[14px] leading-5">
              Wi-Fi password
            </label>
            <p className="text-muted mt-1 text-[12px] leading-4">
              The password used to connect new devices.
            </p>
            <div className="relative mt-2">
              <LockKey
                className="text-muted absolute top-1/2 left-4 size-5 -translate-y-1/2"
                weight="light"
                aria-hidden
              />
              <input
                id="network-password"
                name="network-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setErrors((current) => ({ ...current, password: undefined }));
                }}
                autoComplete="new-password"
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? "network-password-error" : undefined}
                placeholder="Enter your Wi-Fi password"
                className="border-border text-primary placeholder:text-muted focus:border-ring focus:ring-accent/15 h-13 w-full rounded-xl border bg-white pr-12 pl-12 text-[16px] transition outline-none focus:ring-4"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="text-muted hover:text-primary focus-visible:outline-ring absolute top-1/2 right-2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full transition focus-visible:outline-2"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeSlash className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
            {errors.password && (
              <p
                id="network-password-error"
                className="text-destructive mt-2 text-[12px] leading-4"
              >
                {errors.password}
              </p>
            )}
          </div>

          {/* The USR-W610 is 2.4 GHz only. Routers that split their bands into
              two SSIDs are the most common cause of a failed bench setup, so
              the page asks rather than guessing. */}
          <div>
            <label className="border-border-subtle hover:border-border bg-background flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition">
              <input
                type="checkbox"
                checked={hasSplitBands}
                onChange={(event) => setHasSplitBands(event.target.checked)}
                className="border-border accent-accent focus-visible:outline-ring mt-0.5 size-4 shrink-0 rounded focus-visible:outline-2 focus-visible:outline-offset-2"
              />
              <span className="text-primary text-[13px] leading-5">
                My router shows two Wi-Fi names, one ending in 5G or 5GHz.
              </span>
            </label>

            {hasSplitBands && (
              <div className="mt-3">
                <div className="bg-sky-subtle mb-3 rounded-xl p-4">
                  <p className="text-primary text-[13px] leading-5">
                    Please give us the name <span className="font-semibold">without</span> 5G above.
                    The connectors can only join the 2.4 GHz network.
                  </p>
                </div>
                <label
                  htmlFor="network-name-5ghz"
                  className="text-primary block text-[14px] leading-5"
                >
                  The 5 GHz name (optional)
                </label>
                <p className="text-muted mt-1 text-[12px] leading-4">
                  Helps our team recognise your router setup.
                </p>
                <input
                  id="network-name-5ghz"
                  name="network-name-5ghz"
                  value={networkName5ghz}
                  onChange={(event) => setNetworkName5ghz(event.target.value)}
                  autoComplete="off"
                  placeholder="e.g. HomeWiFi-5G"
                  className="border-border text-primary placeholder:text-muted focus:border-ring focus:ring-accent/15 mt-2 h-13 w-full rounded-xl border bg-white px-4 text-[16px] transition outline-none focus:ring-4"
                />
              </div>
            )}
          </div>

          <div>
            <label className="border-border-subtle hover:border-border bg-background flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition">
              <input
                type="checkbox"
                checked={confirmedMainNetwork}
                onChange={(event) => {
                  setConfirmedMainNetwork(event.target.checked);
                  setErrors((current) => ({ ...current, confirmation: undefined }));
                }}
                aria-invalid={Boolean(errors.confirmation)}
                aria-describedby={errors.confirmation ? "confirmation-error" : undefined}
                className="border-border accent-accent focus-visible:outline-ring mt-0.5 size-4 shrink-0 rounded focus-visible:outline-2 focus-visible:outline-offset-2"
              />
              <span className="text-primary text-[13px] leading-5">
                I confirm this is my main home network, not a guest network.
              </span>
            </label>
            {errors.confirmation && (
              <p id="confirmation-error" className="text-destructive mt-2 text-[12px] leading-4">
                {errors.confirmation}
              </p>
            )}
          </div>

          {errors.form && <p className="text-destructive text-[13px] leading-5">{errors.form}</p>}

          <button
            type="submit"
            disabled={submit.isPending}
            className="bg-accent text-accent-foreground hover:bg-accent-pressed focus-visible:outline-ring flex h-13 w-full items-center justify-center gap-2 rounded-md px-6 text-[15px] transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-70"
          >
            {submit.isPending ? "Sharing securely…" : "Share securely with us"}
            {!submit.isPending && <ArrowRight className="size-4" weight="bold" />}
          </button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {TRUST_POINTS.map((label, index) => {
            const ItemIcon = TRUST_ICONS[index];
            return (
              <div key={label} className="text-muted flex items-center gap-2 text-[12px] leading-4">
                {/* `noUncheckedIndexedAccess` makes the lookup optional. Guarding
                    rather than asserting means a future fourth trust point loses
                    its icon instead of crashing the page. */}
                {ItemIcon && (
                  <ItemIcon className="text-primary size-4 shrink-0" weight="light" aria-hidden />
                )}
                {label}
              </div>
            );
          })}
        </div>
      </form>
    </div>
  );
}
